import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {AuthService} from '../core/security/auth.service';
import {Subscription} from 'rxjs';
import {CurrentUser} from '../api/models/current-user';
import moment from 'moment';
import 'twix';
import {ReportsService as ReportsApiService} from '../api/services/reports.service';
import * as _ from 'lodash';
import {HeaderService} from '../core/services/header.service';
import {ModulesEnum} from '../core/security/modules.enum';
import {RolesEnum} from '../core/security/roles.enum';
import {DateTime} from 'luxon';
import {DateUtils} from '../core/utilities/date-time-helpers';
import {LookupService as LookupApiService} from '../api/services/lookup.service';

@Injectable()
export class ReportsService implements OnDestroy {

  lookupUpdatedEvent = new EventEmitter<void>();

  public mobileViewSubscription: Subscription;
  public displaySize = 15;

  public useFilterApplyButton = false;

  public domainsLookup: any[] = [];
  public accountsLookup: any[] = [];
  public groupsAndOtherLookup: any[] = [];
  public extensionsLookup: any[] = [];
  public groupsLookup: any[] = [];
  public huntGroupsLookup: any[] = [];
  public departmentsLookup: any[] = [];
  public departmentsNamesLookup: any[] = [];
  public attendantsLookup: any[] = [];

  public selectedDomain: string | undefined;

  public rangeStartMoment: DateTime;
  public rangeEndMoment: DateTime;
  public rangeStartDate: Date;
  public rangeEndDate: Date;
  public selectedDateRange = {action: 0, text: 'Today', title: 'Today'};
  public dateRanges = [
    {action: 99, text: 'Custom Range', title: 'Custom Range'},
    {action: 0, text: 'Today', title: 'Today'},
    {action: 1, text: 'Yesterday', title: 'Yesterday'},
    {action: 2, text: 'This Week', title: 'This Week'},
    {action: 3, text: 'Last Week', title: 'Last Week'},
    {action: 4, text: 'This Month', title: 'This Month'},
    {action: 5, text: 'Last Month', title: 'Last Month'},
    {action: 6, text: 'Last 60 days', title: 'Last 60 days'},
    {action: 7, text: 'Last 90 days', title: 'Last 90 days'},
  ];

  public userRights = {
    recordingsDaysLimit: 0,
    recordingsEnabled: false,
    recordingsPlay: false,
    recordingsDownload: false,
    recordingsDelete: false,
    surveysEnabled: false,
    surveysView: false,
  };

  private currentUserSubscription: Subscription;

  constructor(private authService: AuthService,
              private reportsApiService: ReportsApiService,
              private lookupApiService: LookupApiService,
              private headerService: HeaderService) {

    this.mobileViewSubscription = this.headerService.isMobileView
      .subscribe((value) => {
        this.displaySize = value ? 10 : 10;
        console.log('displaySize: ' + this.displaySize);
      });

    moment.updateLocale('en', {
      week: {
        dow: 1,
        doy: 1
      },
    });

    this.currentUserSubscription = this.authService.currentUser.subscribe((value?: CurrentUser) => {
      if (value) {
        this.selectedDomain = value.domain;
        this.useFilterApplyButton = value.settings.useManualReportsFiltering || false;
      } else {
        this.selectedDomain = undefined;
        this.useFilterApplyButton = false;
      }

      if (this.selectedDomain) {
        this.pbxAccountLookup();
      }

      if (value) {
        // update user rights
        this.userRights.recordingsDaysLimit = value.recordingsDaysLimit;
        this.userRights.recordingsEnabled = !!value.modules.find((module) => module === ModulesEnum.RecordingsModule);
        this.userRights.recordingsPlay = !!value.roles.find(
          (role) => {
            return role === RolesEnum.Recordings_View
              || role === RolesEnum.Recordings_Edit
              || role === RolesEnum.Recordings_Delete;
          }
        );
        this.userRights.recordingsDownload = !!value.roles.find(
          (role) => {
            return role === RolesEnum.Recordings_Edit
              || role === RolesEnum.Recordings_Delete;
          }
        );
        this.userRights.recordingsDelete = !!value.roles.find((role) => role === RolesEnum.Recordings_Delete);
        this.userRights.surveysEnabled = !!value.modules.find((module) => module === ModulesEnum.SurveysModule);
        this.userRights.surveysView = !!value.roles.find((role) => role === RolesEnum.Surveys_View
          || role === RolesEnum.Surveys_Edit
          || role === RolesEnum.Surveys_Delete);
      } else {
        this.userRights = {
          recordingsDaysLimit: 0,
          recordingsEnabled: false,
          recordingsPlay: false,
          recordingsDownload: false,
          recordingsDelete: false,
          surveysEnabled: false,
          surveysView: false
        };
      }
    });

    this.onDateRangeChange(this.dateRanges[1]);
  }

  async pbxAccountLookup() {
    const domainsResponse = await this.lookupApiService.lookupCustomerDomains().toPromise();
    if (domainsResponse.total > 0) {
      this.domainsLookup = domainsResponse.data.map(x => {
        return {
          text: x.text,
          value: x.id,
          id: _.uniqueId('id_')
        };
      })
    }
    const response = await this.reportsApiService.reportsGetPbxAccountData({domain: this.selectedDomain || 'unknown'}).toPromise();

    this.accountsLookup = response.map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    this.attendantsLookup = response.filter((x) => x.type === 'attendants').map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    this.extensionsLookup = response.filter((x) => x.type === 'extensions').map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    this.groupsAndOtherLookup = response.filter((x) => x.type !== 'extensions').map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    this.groupsLookup = response.filter((x) => x.type === 'acds').map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    this.huntGroupsLookup = response.filter((x) => x.type === 'hunts').map((x) => {
      let name = x.fullName;
      if (x.firstName || x.displayName) {
        name = `${x.firstName || ''} ${x.displayName || ''}`;
      }

      return {
        text: `${name || ''} (${x.extension})`.trim(),
        value: x.extension,
        id: _.uniqueId('id_')
      };
    });

    const departments = response.filter((x) => x.type === 'extensions' && x.position).map((x) => {
      return {
        text: `${x.position || ''}`.trim(),
        value: x.extension
      };
    });

    // const departmentsGroup = _.groupBy(departments, 'text');

    const grouped = _.mapValues(_.groupBy(departments, 'text'),
      item => {
        const values = item.map(car => _.omit(car, 'text'));
        const id = _.uniqueId('id_');
        return {
          text: item[0].text,
          value: id,
          extensions: values.map((x) => x.value),
          id: id
        };
      });

    const groupedDepNames = _.mapValues(_.groupBy(departments, 'text'),
      item => {
        const values = item.map(car => _.omit(car, 'text'));
        const id = _.uniqueId('id_');
        return {
          text: item[0].text,
          value: item[0].text,
          extensions: values.map((x) => x.value),
          id: id
        };
      });

    this.departmentsLookup = _.values(grouped);
    this.departmentsNamesLookup = _.values(groupedDepNames);

    this.lookupUpdatedEvent.emit();
  }

  getDepartmentsExtensions(departments: string[]) {
    const selectedDepartments = this.departmentsLookup.filter((item) => {
      return departments.indexOf(item.value) > -1;
    });

    let selectedExtensions = [];

    for (const department of selectedDepartments) {
      selectedExtensions = _.union(selectedExtensions, department.extensions);
    }

    return selectedExtensions;
  }

  getExtensionName(extension: string) {
    const lookup = this.accountsLookup.find((item) => item.value === extension);
    if (lookup) {
      return lookup.text;
    }
    return extension;
  }

  onDateRangeChange(selectedItem: any) {
    console.log(selectedItem);

    const periodType = selectedItem.action;

    if (periodType !== 99) {
      this.selectedDateRange = selectedItem;
    }

    if (periodType === 0) { // today
      this.rangeStartMoment = DateTime.local().startOf('day');
      this.rangeEndMoment = DateTime.local().startOf('day').plus({'days': 1});
    } else if (periodType === 1) { // yesterday
      this.rangeStartMoment = DateTime.local().startOf('day').plus({'days': -1});
      this.rangeEndMoment = DateTime.local().startOf('day');
    } else if (periodType === 2) { // This Week
      this.rangeStartMoment = DateTime.local().startOf('week');
      this.rangeEndMoment = DateTime.local().endOf('week').startOf('day').plus({'days': 1});
    } else if (periodType === 3) { // Last Week
      this.rangeStartMoment = DateTime.local().plus({'weeks': -1}).startOf('week');
      this.rangeEndMoment = DateTime.local().plus({'weeks': -1}).endOf('week').startOf('day').plus({'days': 1});
    } else if (periodType === 4) { // This Month
      this.rangeStartMoment = DateTime.local().startOf('month');
      this.rangeEndMoment = DateTime.local().endOf('month').startOf('day').plus({'days': 1});
    } else if (periodType === 5) { // Last Month
      this.rangeStartMoment = DateTime.local().plus({'months': -1}).startOf('month');
      this.rangeEndMoment = DateTime.local().plus({'months': -1}).endOf('month').startOf('day').plus({'days': 1});
    } else if (periodType === 6) { // Last 60 days
      this.rangeStartMoment = DateTime.local().startOf('day').plus({'days': -61});
      this.rangeEndMoment = DateTime.local().startOf('day').plus({'days': 1});
    } else if (periodType === 7) { // Last 90 days
      this.rangeStartMoment = DateTime.local().startOf('day').plus({'days': -91});
      this.rangeEndMoment = DateTime.local().startOf('day').plus({'days': 1});
    }

    if (periodType !== 99) {
    }

    console.log(this.rangeStartMoment.toISO());
    console.log(this.rangeEndMoment.toISO());

    this.rangeStartDate = DateUtils.localDateFromDateTime(this.rangeStartMoment);
    this.rangeEndDate = DateUtils.localDateFromDateTime(this.rangeEndMoment);
  }

  ngOnDestroy() {
    console.log('DESTROYED THE ReportsService');
    this.mobileViewSubscription?.unsubscribe();
  }
}
